import { proxy } from 'valtio';

import systemPromptDefault from '../../systemprompt.txt?raw';
import { editor } from 'monaco-editor';
import { sessionState } from '@/app/sessionState';
import { get } from '@/utils/fetch';

// Add messages to the editor state structure
export const editorState = proxy({
  component: {
    specification: "",
    jsx: "",
    id: "",
    props: {}
  },
  demoData: "",
  prompts: {
    system: systemPromptDefault,
  },
  editCmd: "",
  toolbar: {
    undoDisabled: true,
    redoDisabled: true
  },
  history: {
    versions: [],
    activeVersion: -1
  },
  //lastUpdated: Date.now(),
  models: [  // Add this for the list of models
    "anthropic/claude-sonnet-4",
    "anthropic/claude-3.7-sonnet:thinking",
    { value: "__", label: "Default" },
    { value: "_", label: "Custom" },
    "google/gemini-2.5-flash",
    "google/gemini-2.5-pro",
    "qwen/qwen3-coder@cerebras",
    "openai/gpt-5",
    "openrouter/horizon-beta",
    "z-ai/glm-4.5",
    "z-ai/glm-4.5-air",
    "deepseek/deepseek-r1-0528",    
    "moonshotai/kimi-k2",
    "openai/o1-mini-2024-09-12",
    "deepseek/deepseek-r1:free"
  ],
  modelName: '__',
  chat: {
    messages: []
  },
  pushHistory: (force) => {

    if (!force) {
      // Condition a: when current component specification or jsx are empty do nothing
      if (!editorState.component.specification || !editorState.component.jsx) {
        return editorState.history.activeVersion;
      }

      // Condition b: when the current component's specification AND jsx match the value of the activeVersion do nothing
      // This check is only relevant if there is an active version to compare against.
      if (editorState.history.activeVersion >= 0 &&
        editorState.history.activeVersion < editorState.history.versions.length) {
        const currentActiveVersionState = editorState.history.versions[editorState.history.activeVersion];
        if (editorState.component.specification === currentActiveVersionState.specification &&
          editorState.component.jsx === currentActiveVersionState.jsx) {
          return editorState.history.activeVersion;
        }
      }
    }

    var newState = {
      specification: editorState.component.specification,
      jsx: editorState.component.jsx,
    }

    editorState.history.versions.push(newState);
    editorState.history.activeVersion = editorState.history.versions.length - 1;
    // Update toolbar states
    // undoDisabled should be true if activeVersion is 0 (first item), false otherwise.
    editorState.toolbar.undoDisabled = editorState.history.activeVersion < 1;
    editorState.toolbar.redoDisabled = true; // Disable redo since we're at the newest state
    logState('pushing history');

    return editorState.history.activeVersion; // c. return activeVersion
  },
  undoHistory: () => {

    // check if we have an unsaved state
    if (editorState.history.activeVersion == editorState.history.versions.length - 1 && (
      editorState.component.specification != editorState.history.versions[editorState.history.activeVersion].specification ||
      editorState.component.jsx != editorState.history.versions[editorState.history.activeVersion].jsx)
    ) {
      editorState.pushHistory();
    }

    if (editorState.history.activeVersion > 0) {
      editorState.history.activeVersion--;
      editorState.component.specification = editorState.history.versions[editorState.history.activeVersion].specification;
      editorState.component.jsx = editorState.history.versions[editorState.history.activeVersion].jsx;
      editorState.toolbar.redoDisabled = false;
      editorState.toolbar.undoDisabled = editorState.history.activeVersion < 1;
    }
    logState('undoing history');
  },
  redoHistory: () => {
    if (editorState.history.activeVersion < editorState.history.versions.length - 1) {
      editorState.history.activeVersion++;
      editorState.updateSpecification(editorState.history.versions[editorState.history.activeVersion].specification);
      editorState.component.jsx = editorState.history.versions[editorState.history.activeVersion].jsx;
      editorState.toolbar.undoDisabled = false;
      editorState.toolbar.redoDisabled = editorState.history.activeVersion === editorState.history.versions.length - 1;
    }
    logState('redoing history');
  },
  selectHistoryVersion: (newActiveVersion) => {
    if (newActiveVersion === editorState.history.activeVersion) {
      return; // No change needed
    }

    if (newActiveVersion >= 0 && newActiveVersion < editorState.history.versions.length) {
      // If the current version is the latest and has unsaved changes, push it to history first.
      const currentHistoryVersion = editorState.history.versions[editorState.history.activeVersion];
      if (
        editorState.history.activeVersion === editorState.history.versions.length - 1 &&
        currentHistoryVersion &&
        (editorState.component.specification !== currentHistoryVersion.specification ||
          editorState.component.jsx !== currentHistoryVersion.jsx)
      ) {
        editorState.pushHistory();
        // After pushHistory, editorState.history.activeVersion points to the new latest version.
        // newActiveVersion index should still be valid relative to the (now longer) versions array.
      }

      editorState.history.activeVersion = newActiveVersion;
      const selectedVersionData = editorState.history.versions[newActiveVersion];
      // Use updateSpecification to ensure props and potentially other derived states are updated
      editorState.updateSpecification(selectedVersionData.specification);
      editorState.component.jsx = selectedVersionData.jsx;

      editorState.toolbar.undoDisabled = newActiveVersion < 1;
      editorState.toolbar.redoDisabled = newActiveVersion === editorState.history.versions.length - 1;
    }
    logState('selecting history version to: ' + newActiveVersion);
  },
  updateSpecification: (specification) => {    
    editorState.component.specification = specification;

    let newProps = {};
    let props = extractProps(specification);
    if (props) {
      try {
        props = addMissingQuotes(props);
        newProps = JSON.parse(props);
      } catch (e) {
        console.error(e.message + ": " + props);
      }
    }

    editorState.component.props = newProps;
    getJsonSample();
  },
  updateEditCmd: (text) => {    
    editorState.editCmd = text;
    getJsonSample();
  },
  clearHistory: () => {
    editorState.history.versions = [];
    editorState.history.activeVersion = -1;
    editorState.toolbar.undoDisabled = true;
    editorState.toolbar.redoDisabled = true;
    editorState.chat.messages = [];
    logState('clearing history');
  },
  // Add a method to update messages
  updateMessages: (newMessages) => {
    editorState.chat.messages = newMessages;
  },
  addMessage: (message) => {
    editorState.chat.messages.push(message);
    console.log('Message added:', message);
  },
  clearMessages: () => {
    editorState.chat.messages = [];
  },
  refresh: () => {
    editorState.lastUpdated = new Date().toISOString();
  },
  getSampleData: async () => {
    return await getJsonSample();
  },
});

function logState(msg) {
  return;
  console.log(msg + ': active state:', editorState.history.activeVersion);
  console.log('History states:');
  editorState.history.versions.forEach((state, index) => {
    console.log(`[${index}] JSX:`, state.jsx);
  });
}

async function getJsonSample() {
  const prompt = editorState.component.specification + " " + editorState.editCmd;

  // Find URLs starting with http:// or https://
  const urlRegex = /https?:\/\/[^\s"'<>)]+/g;
  const urls = prompt.match(urlRegex);

  if (!urls || urls.length === 0) {
    sessionState.demoData.dataUrl = "";
    sessionState.demoData.data = "{}";
    editorState.demoData = sessionState.demoData.data;
    return sessionState.demoData.data;
  }

  try {
    // Fetch last URL found
    const url = urls[urls.length - 1];

    if (sessionState.demoData.dataUrl != url || !sessionState.demoData.data || sessionState.demoData.data === "{}") {
      sessionState.demoData.dataUrl = url;
      var jsonData = await get(url);

      if (jsonData.ErrorCode === 0) {
        jsonData = jsonData.Data;
      }

      sessionState.demoData.data = JSON.stringify(jsonData, null, 2);
    }

  } catch (error) {
    console.error('Error fetching JSON data:', error);
    sessionState.demoData.data = "{}";
  }

  editorState.demoData = sessionState.demoData.data;

  return sessionState.demoData.data;
}

function extractProps(text) {
  // Match 'props:' or 'props=' followed by any whitespace (including newlines)
  const propsMatch = text.match(/props[=:]/);
  if (!propsMatch) return null;

  // Find the first '{' after the props prefix
  const afterPrefix = text.slice(propsMatch.index + propsMatch[0].length);
  const firstBrace = afterPrefix.match(/\s*{/);
  if (!firstBrace) return null;

  const startIndex = propsMatch.index + propsMatch[0].length + firstBrace.index + firstBrace[0].length - 1;
  let braceCount = 0;
  let extracted = '';
  let inString = false;
  let stringChar = '';

  for (let i = startIndex; i < text.length; i++) {
    const char = text[i];

    // Handle string boundaries
    if ((char === '"' || char === "'") && text[i - 1] !== '\\') {
      if (!inString) {
        inString = true;
        stringChar = char;
      } else if (char === stringChar) {
        inString = false;
      }
    }

    // Decide whether to keep the character
    if (inString) {
      // Keep everything inside strings
      extracted += char;
    } else if (char === '\n' || char === '\r') {
      // Skip newlines when not in strings
      continue;
    } else {
      // Keep all other characters
      extracted += char;
    }

    if (!inString) {
      if (char === '{') {
        braceCount++;
      } else if (char === '}') {
        braceCount--;
        if (braceCount === 0) {
          return extracted;
        }
      }
    }
  }

  return null;
}

function addMissingQuotes(jsonStr) {
  // Replace unquoted property names with quoted ones
  // (?<!") - negative lookbehind to avoid matching already quoted properties
  // \b - word boundary
  // [a-zA-Z_]\w* - property name starting with letter/underscore, followed by word chars
  // \b - word boundary
  // (?=\s*:) - positive lookahead for colon with optional whitespace
  return jsonStr.replace(/(?<!")(\b[a-zA-Z_]\w*\b)(?=\s*:)/g, '"$1"');
}